import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import AdminCasePage from "components/pages/admin/AdminCasePage";
import AdminPage from "components/pages/admin/AdminPage";
import AIOutputPage from "components/pages/AIOutputPage";
import CasePage from "components/pages/CasePage";
import CaseReportPage from "components/pages/CaseReportPage";
import CasesPage from "components/pages/CasesPage";
import ChangePasswordPage from "components/pages/ChangePasswordPage";
import FinalReportPage from "components/pages/FinalReportPage";
import FourOhFourPage from "components/pages/FourOhFourPage";
import HandbookPage from "components/pages/HandbookPage";
import ReportListPage from "components/pages/ReportListPage";
import ResetPasswordPage from "components/pages/ResetPasswordPage";
import SlideViewerPage from "components/pages/SlideViewerPage";
import ApplicationState from "store/ApplicationState";

import "./base.scss";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const RedirectLegacyCase = () => {
  const { case_id } = useParams();
  return <Navigate to={`/cases/${case_id}`} replace={true} />;
};

export const LongLivedTokenRoutes = () => (
  <SentryRoutes>
    <Route
      path="cases/:case_id/report/:report_id"
      element={<FinalReportPage />}
    />
    <Route path="*" element={<FourOhFourPage />} />
  </SentryRoutes>
);

export const AppRoutes = () => {
  const features = useSelector(({ sys }: ApplicationState) => sys.features);

  const user = useSelector(({ auth }: ApplicationState) => auth.user);

  return (
    <SentryRoutes>
      <Route
        key="/reset-password"
        path="/password-reset"
        element={<ResetPasswordPage />}
      />
      <Route
        key="/change-password"
        path="/password-reset/change-password/:uid/:token"
        element={<ChangePasswordPage />}
      />
      {[
        "/slide/:slide_id",
        "/slides/:slide_id",
        "/histoslide/slide/:slide_id",
      ].map((path, index) => (
        <Route
          path={path}
          key={`viewer_${index}`}
          element={<SlideViewerPage />}
        />
      ))}

      <Route path="cases" element={<CasePage />}>
        <Route path=":case_id" element={<SlideViewerPage />} />
        <Route path=":case_id/preview" element={<CaseReportPage />} />
        <Route path=":case_id/slides/:slide_id" element={<SlideViewerPage />} />
        <Route
          path=":case_id/documents/:document_id"
          element={<SlideViewerPage />}
        />
      </Route>

      {/* Due to an issue with CSS cascading and the bootstrap min file, this
        route is required to keep the case report page print preview working properly */}
      {/* TODO: investigate why this page is necessary for the CSS cascade and further how
        to rid ourselves of the bootstrap file for good or at least control its cascade timing */}
      <Route
        key="ai-output"
        path="/cases/:case_id/slides/:slide_id/ai/:classification_id"
        element={<AIOutputPage />}
      />

      <Route
        path="cases/:case_id/report/:report_id"
        element={<FinalReportPage />}
      />

      {user &&
        user.has_access_to_report_list &&
        (features?.clinicReportsList || features?.clinicRequisitionsList) && (
          <>
            <Route
              path="reports"
              element={<ReportListPage reportType="report" />}
            />
            <Route
              path="requisitions"
              element={<ReportListPage reportType="requisition" />}
            />
          </>
        )}

      {/* Legacy /case/:case_id redirect just in case it's used _somewhere_ */}
      <Route path="/case/:case_id" element={<RedirectLegacyCase />} />

      {[
        "",
        "all",
        "active",
        "working",
        "flagged",
        "finalized",
        "screen",
        "screened",
      ].map((path, index) => (
        <Route
          path={`/${path}`}
          key={`caseList_${index}`}
          element={<CasesPage queue={path} />}
        />
      ))}

      <Route path="/handbook" element={<HandbookPage />} />

      {features?.frontendAdmin && (
        <Route path="/admin">
          <Route path="" element={<AdminPage list="cases" />} />

          <Route path="orders" element={<AdminPage list="orders" />} />
          <Route path="cases" element={<AdminPage list="cases" />} />
          <Route path="slides" element={<AdminPage list="slides" />} />
          <Route
            path="slides/:slide_id"
            element={<AdminPage list="slides" />}
          />
          <Route
            path="cases/slides/:slide_id"
            element={<AdminPage list="cases" />}
          />
          <Route path="cases/:case_id" element={<AdminCasePage />} />
          <Route
            path="cases/:case_id/slides/:slide_id"
            element={<AdminCasePage />}
          />
          <Route
            path="cases/:case_id/specimens/:specimen_id"
            element={<AdminCasePage />}
          />
          <Route path="documents" element={<AdminPage list="documents" />} />
          <Route
            path="documents/:document_id"
            element={<AdminPage list="documents" />}
          />
          <Route
            path="cases/:case_id/documents/:document_id"
            element={<AdminCasePage />}
          />
        </Route>
      )}
      <Route path="*" element={<FourOhFourPage />} />
    </SentryRoutes>
  );
};

export default AppRoutes;
